import { useEffect, useRef, useState, type FormEvent } from "react";
import client from "libs/client";
import queryClient from "libs/queryClient";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import { buildProgramGamePostBody, programGameErrorMessage } from "../formUtils";
import type { OwnershipOption, ProgramGameFormState } from "../types";

interface UseProgramGameFormSubmitParams {
	isOpen: boolean;
	isAdminUser: boolean;
	ownershipItems: OwnershipOption[];
	form: ProgramGameFormState;
	hasErrors: boolean;
	onClose: () => void;
}

export function useProgramGameFormSubmit({
	isOpen,
	isAdminUser,
	ownershipItems,
	form,
	hasErrors,
	onClose,
}: UseProgramGameFormSubmitParams) {
	const { addSuccessToast, addErrorToast } = useToast();
	const isSubmittingRef = useRef(false);
	const createProgramGameMutation = client.postDiscoverProgramGame.useMutation({});

	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!isOpen) {
			isSubmittingRef.current = false;
			return;
		}
		setIsSubmitted(false);
		setIsSubmitting(false);
		isSubmittingRef.current = false;
	}, [isOpen]);

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (isSubmitting || isSubmittingRef.current) {
			return;
		}
		setIsSubmitted(true);
		if (hasErrors) {
			return;
		}

		const ownershipIdsForSubmit = isAdminUser
			? form.ownershipSelections.map((o) => Number(o.value))
			: ownershipItems.length > 0
				? [Number(ownershipItems[0].value)]
				: [];

		if (ownershipIdsForSubmit.length === 0) {
			addErrorToast(_("discover.gamesTable.programGame.error.ownershipRequired"));
			return;
		}

		isSubmittingRef.current = true;
		setIsSubmitting(true);

		const body = buildProgramGamePostBody(form, ownershipIdsForSubmit);

		try {
			await createProgramGameMutation.mutateAsync({ body });
			addSuccessToast(_("message.status.update.success"));
			await queryClient.invalidateQueries({ queryKey: ["getDiscoverGamesDynamic"] });
			onClose();
		} catch (error: unknown) {
			addErrorToast(programGameErrorMessage(error));
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	return { isSubmitted, isSubmitting, handleSubmit };
}
